import BackHandOutlinedIcon from '@mui/icons-material/BackHandOutlined';
import BrushIcon from '@mui/icons-material/Brush';
import DeleteIcon from '@mui/icons-material/Delete';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import HighlightAltIcon from '@mui/icons-material/HighlightAlt';
import PentagonOutlinedIcon from '@mui/icons-material/PentagonOutlined';
import SquareIcon from '@mui/icons-material/Square';

import {
  MOVE_TO_DELETE_MODE,
  MOVE_TO_MODIFY_MODE,
  MOVE_TO_POLYGON_MODE,
  MOVE_TO_ROI_MODE,
} from 'components/Procedure/SlidesViewer/HintsFooter/shortcutsConsts';
import { first } from 'es-toolkit/compat';
import { keys, map } from 'lodash';
import { SlideInteractionMenuOption } from './SlideInteractionOption';

export enum SlideAnnotationEditType {
  Delete = 'delete',
  Modify = 'modify',
  Polygon = 'polygon',
  Roi = 'roi',
  Point = 'point',
  Brush = 'brush',
  Move = 'move',
  Select = 'select',
  ReopenClockwise = 'reopenClockwise',
  ReopenCounterclockwise = 'reopenCounterclockwise',
}

export const reopenEditTypes = [
  SlideAnnotationEditType.ReopenClockwise,
  SlideAnnotationEditType.ReopenCounterclockwise,
];

// TODO: move this to a database entry (perhaps under UI settings)
export const RoiFixedSizes: { [key: string]: [number, number] } = {
  ['100 x 100']: [100, 100],
  ['120 x 120']: [120, 120],
  ['150 x 150']: [150, 150],
  ['300 x 300']: [300, 300],
  ['500 x 500']: [500, 500],
};

// Brush radius presets, in microns (converted to pixels the same way RoiFixedSizes is: divided by slideMaxResolution).
export const BrushRadiusSizes: { [key: string]: number } = {
  ['10 µm']: 10,
  ['25 µm']: 25,
  ['50 µm']: 50,
  ['100 µm']: 100,
};
export const DEFAULT_BRUSH_RADIUS_MICRONS = 25;

// This is an internal type that we save inside properties of a feature.
// It is taken from the legacy backoffice codebase.
export enum ShapeType {
  Polygon = 'polygon',
  Rect = 'rect',
  Point = 'circle',
}

export const editTypeToShapeTypeMap = {
  [SlideAnnotationEditType.Polygon]: ShapeType.Polygon,
  [SlideAnnotationEditType.Roi]: ShapeType.Rect,
  [SlideAnnotationEditType.Point]: ShapeType.Point,
  [SlideAnnotationEditType.Brush]: ShapeType.Polygon,
};

export const shapeTypeToEditTypeMap = {
  [ShapeType.Polygon]: SlideAnnotationEditType.Polygon,
  [ShapeType.Rect]: SlideAnnotationEditType.Roi,
  [ShapeType.Point]: SlideAnnotationEditType.Point,
};

// Polygon / ROI / Point put the viewer into that draw mode; the active annotation class (picked
// from the quick menu once drawing) determines which class a newly drawn shape is assigned to.
export const slideAnnotationOptions: SlideInteractionMenuOption[] = [
  {
    editType: SlideAnnotationEditType.Delete,
    title: 'Delete Annotations',
    icon: DeleteIcon,
    shortcutLabel: first(MOVE_TO_DELETE_MODE)?.toUpperCase(),
  },
  {
    editType: SlideAnnotationEditType.Modify,
    title: 'Modify Annotations',
    icon: EditOutlinedIcon,
    shortcutLabel: first(MOVE_TO_MODIFY_MODE)?.toUpperCase(),
  },
  {
    editType: SlideAnnotationEditType.Move,
    title: 'Move Annotations',
    icon: BackHandOutlinedIcon,
  },
  {
    editType: SlideAnnotationEditType.Polygon,
    title: 'Polygon',
    icon: PentagonOutlinedIcon,
    shortcutLabel: first(MOVE_TO_POLYGON_MODE)?.toUpperCase(),
  },
  {
    editType: SlideAnnotationEditType.Roi,
    title: 'ROI Selection',
    icon: SquareIcon,
    shortcutLabel: first(MOVE_TO_ROI_MODE)?.toUpperCase(),
    subOptions: map(keys(RoiFixedSizes), (title) => ({
      subEditType: title,
      title: title,
    })),
  },
  {
    editType: SlideAnnotationEditType.Point,
    title: 'Point',
    icon: FiberManualRecordIcon,
  },
  {
    editType: SlideAnnotationEditType.Brush,
    title: 'Brush. Scroll to resize. Shift pushes inward. Ctrl stops at neighboring polygons.',
    icon: BrushIcon,
    subOptions: map(keys(BrushRadiusSizes), (title) => ({
      subEditType: title,
      title: title,
    })),
  },
  {
    editType: SlideAnnotationEditType.Select,
    title: 'Select multiple annotations',
    icon: HighlightAltIcon,
  },
];
